/**
 * IntegrationsService
 *
 * Typed helper for reading/writing third-party integration credentials
 * to the `integrations` table in mycrogym_studio_config IndexedDB.
 *
 * SECURITY: credentials are stored LOCALLY ONLY — never transmitted to the server.
 *
 * Key convention:  '<provider>.<companyid>.<uuid>'
 *   e.g. 'email.gmail.abc123.uuid1'   – one Gmail account
 *        'payment.stripe.abc123'       – Stripe (one per company)
 *        'bank.finapi.abc123'          – finAPI (one per company)
 */

import DBConfigService from './DBConfigService'

// ── Shared ─────────────────────────────────────────────────────────────────────

export type IntegrationProvider = 'email' | 'payment' | 'bank' | 'meeting'

export interface Integration<T = Record<string, any>> {
    key: string
    provider: IntegrationProvider
    companyid: string
    label: string
    config: T
    enabled: boolean
    updated: string
}

// ── Config shapes ──────────────────────────────────────────────────────────────

export interface GmailConfig {
    /** Gmail address this account is linked to */
    email: string
    /** Refresh token — obtained after the OAuth2 consent flow via the server */
    refreshToken: string
    /** Display name returned from Google (optional, set after first auth) */
    displayName?: string
}

export interface ImapConfig {
    protocol: 'imap' | 'pop3'
    incomingHost: string
    incomingPort: number
    incomingTls: boolean
    smtpHost: string
    smtpPort: number
    smtpTls: boolean
    username: string
    /** Stored as token, never displayed after save */
    appToken: string
}

export interface StripeConfig {
    /** Publishable key (safe to display) */
    publishableKey: string
    /** Secret key — stored as token, never displayed */
    secretKey: string
    /** Optional webhook signing secret */
    webhookSecret: string
    environment: 'test' | 'live'
}

export interface PaypalConfig {
    clientId: string
    /** Client secret — stored as token, never displayed */
    clientSecret: string
    environment: 'sandbox' | 'live'
}

export interface FinapiBankAccount {
    id: string       // FinAPI account ID (stringified number)
    iban: string
    name: string     // e.g. "Girokonto"
    bankName: string
    enabled: boolean // whether to include when loading transactions
}

export interface FinapiBankConnection {
    id: string       // FinAPI bankConnectionId (stringified number)
    bankName: string
    lastUpdated?: string  // ISO date
    accounts: FinapiBankAccount[]
}

export interface FinapiConfig {
    /** FinAPI user ID — created once per company on first bank connection */
    userId?: string
    /** FinAPI user password — auto-generated, never displayed after creation */
    userSecret?: string
    /** Connected bank connections (multiple per company) */
    connections: FinapiBankConnection[]
}

export interface SumupReaderEntry {
    id: string
    name: string
    status?: string
    /** ID of the gym location this reader is assigned to */
    locationId?: string
}

export interface SumupConfig {
    /** API key: sk_test_… or sk_live_… */
    apiKey: string
    /** Merchant Code from SumUp Dashboard */
    merchantCode: string
    environment: 'test' | 'live'
    /** Affiliate Key from SumUp Developer Portal → Affiliate Keys */
    affiliateKey?: string
    /** App ID from SumUp Developer Portal → Affiliate Keys */
    appId?: string
    /** Registered card readers (persisted locally so CashPoint can use them offline) */
    readers?: SumupReaderEntry[]
}

export interface ZoomConfig {
    /** Zoom API Key (JWT or Server-to-Server OAuth Client ID) */
    apiKey: string
    /** Zoom API Secret — stored as token, never displayed */
    apiSecret: string
    /** Account email linked to the Zoom account */
    accountEmail?: string
    environment: 'test' | 'live'
}

export interface GoogleMeetConfig {
    /** Google OAuth2 Client ID */
    clientId: string
    /** Google OAuth2 Client Secret — stored as token, never displayed */
    clientSecret: string
    /** Account email linked to the Google account */
    accountEmail?: string
}

// ── Service ────────────────────────────────────────────────────────────────────

export class IntegrationsService {

    // ── Generic CRUD ──────────────────────────────────────────────────────────────

    static async get<T>(key: string): Promise<Integration<T> | null> {
        return DBConfigService.getIntegration(key)
    }

    static async set<T>(entry: Omit<Integration<T>, 'updated'>): Promise<void> {
        return DBConfigService.setIntegration(
            entry.key, entry.provider, entry.companyid,
            entry.config as any, entry.enabled
        ).then(() => DBConfigService.setIntegration(
            entry.key, entry.provider, entry.companyid,
            { ...(entry.config as any), _label: entry.label } as any,
            entry.enabled
        ))
        // Simpler: use raw put via DBConfigService which accepts full shape
    }

    static async put<T>(entry: Omit<Integration<T>, 'updated'>): Promise<void> {
        await DBConfigService.setIntegration(
            entry.key, entry.provider, entry.companyid,
            { ...entry.config as any, _label: entry.label },
            entry.enabled
        )
    }

    static async remove(key: string): Promise<void> {
        return DBConfigService.deleteIntegration(key)
    }

    static async allByProvider(provider: IntegrationProvider, companyid: string): Promise<Integration[]> {
        const list = await DBConfigService.getIntegrationsByProvider(provider, companyid)
        return list.map(r => ({
            ...r,
            label: r.config?._label || r.key
        }))
    }

    // ── Gmail (multiple per company) ───────────────────────────────────────────

    static gmailKey(companyid: string, id: string) { return `email.gmail.${companyid}.${id}` }

    static async allGmail(companyid: string): Promise<Integration<GmailConfig>[]> {
        const all = await DBConfigService.getIntegrationsByProvider('email', companyid)
        return all.filter(r => r.key.includes('.gmail.')).map(r => ({
            ...r,
            label: r.config?._label || r.config?.email || 'Gmail'
        })) as Integration<GmailConfig>[]
    }

    static async setGmail(companyid: string, id: string, label: string, config: GmailConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(
            this.gmailKey(companyid, id), 'email', companyid,
            { ...config, _label: label }, enabled
        )
    }

    static async removeGmail(companyid: string, id: string): Promise<void> {
        return this.remove(this.gmailKey(companyid, id))
    }

    // ── IMAP / POP3 (multiple per company) ───────────────────────────────────

    static imapKey(companyid: string, id: string) { return `email.imap.${companyid}.${id}` }

    static async allImap(companyid: string): Promise<Integration<ImapConfig>[]> {
        const all = await DBConfigService.getIntegrationsByProvider('email', companyid)
        return all.filter(r => r.key.includes('.imap.')).map(r => ({
            ...r,
            label: r.config?._label || r.config?.username || 'IMAP'
        })) as Integration<ImapConfig>[]
    }

    static async setImap(companyid: string, id: string, label: string, config: ImapConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(
            this.imapKey(companyid, id), 'email', companyid,
            { ...config, _label: label }, enabled
        )
    }

    static async removeImap(companyid: string, id: string): Promise<void> {
        return this.remove(this.imapKey(companyid, id))
    }

    // ── Default email (one per company) ───────────────────────────────────────

    static defaultEmailKey(companyid: string) { return `email.default.${companyid}` }

    /** Returns the integration id (uuid) of the chosen default outbound email account, or null. */
    static async getDefaultEmail(companyid: string): Promise<string | null> {
        const entry = await DBConfigService.getIntegration(this.defaultEmailKey(companyid))
        return (entry?.config as any)?.id || null
    }

    /** Persists the default email account id. Pass null to clear. */
    static async setDefaultEmail(companyid: string, id: string | null): Promise<void> {
        if (!id) {
            await DBConfigService.deleteIntegration(this.defaultEmailKey(companyid))
        } else {
            await DBConfigService.setIntegration(
                this.defaultEmailKey(companyid), 'email', companyid, { id }, true
            )
        }
    }


    static stripeKey(companyid: string) { return `payment.stripe.${companyid}` }

    static async getStripe(companyid: string): Promise<Integration<StripeConfig> | null> {
        return this.get<StripeConfig>(this.stripeKey(companyid))
    }

    static async setStripe(companyid: string, config: StripeConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(this.stripeKey(companyid), 'payment', companyid, config, enabled)
    }

    // ── PayPal (one per company) ───────────────────────────────────────────────

    static paypalKey(companyid: string) { return `payment.paypal.${companyid}` }

    static async getPaypal(companyid: string): Promise<Integration<PaypalConfig> | null> {
        return this.get<PaypalConfig>(this.paypalKey(companyid))
    }

    static async setPaypal(companyid: string, config: PaypalConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(this.paypalKey(companyid), 'payment', companyid, config, enabled)
    }

    // ── finAPI (one credential set per company, multiple bank connections) ────────

    static finapiKey(companyid: string) { return `bank.finapi.${companyid}` }

    static async getFinapi(companyid: string): Promise<Integration<FinapiConfig> | null> {
        const raw = await this.get<FinapiConfig>(this.finapiKey(companyid))
        if (!raw) return null
        // Ensure connections is always an array (migrations from old single-account config)
        if (!Array.isArray((raw.config as any).connections)) {
            (raw.config as any).connections = []
        }
        return raw
    }

    static async setFinapi(companyid: string, config: FinapiConfig, enabled = true): Promise<void> {
        if (!Array.isArray(config.connections)) config.connections = []
        await DBConfigService.setIntegration(this.finapiKey(companyid), 'bank', companyid, config, enabled)
    }

    static async removeFinapi(companyid: string): Promise<void> {
        return this.remove(this.finapiKey(companyid))
    }

    /** Persist updated bank connections back into the stored FinAPI config */
    static async updateFinapiConnections(
        companyid: string,
        connections: FinapiBankConnection[]
    ): Promise<void> {
        const existing = await this.getFinapi(companyid)
        if (!existing) return
        await this.setFinapi(companyid, { ...existing.config, connections }, existing.enabled)
    }

    /** Persist userId + userSecret after first-time FinAPI user creation */
    static async updateFinapiUser(
        companyid: string,
        userId: string,
        userSecret: string
    ): Promise<void> {
        const existing = await this.getFinapi(companyid)
        if (!existing) return
        await this.setFinapi(companyid, { ...existing.config, userId, userSecret }, existing.enabled)
    }

    // ── SumUp (one per company) ────────────────────────────────────────────────

    static sumupKey(companyid: string) { return `payment.sumup.${companyid}` }

    static async getSumup(companyid: string): Promise<Integration<SumupConfig> | null> {
        return this.get<SumupConfig>(this.sumupKey(companyid))
    }

    static async setSumup(companyid: string, config: SumupConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(this.sumupKey(companyid), 'payment', companyid, config, enabled)
    }

    static async removeSumup(companyid: string): Promise<void> {
        return this.remove(this.sumupKey(companyid))
    }

    // ── Zoom (one per company) ─────────────────────────────────────────────────

    static zoomKey(companyid: string) { return `meeting.zoom.${companyid}` }

    static async getZoom(companyid: string): Promise<Integration<ZoomConfig> | null> {
        return this.get<ZoomConfig>(this.zoomKey(companyid))
    }

    static async setZoom(companyid: string, config: ZoomConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(this.zoomKey(companyid), 'meeting', companyid, config, enabled)
    }

    static async removeZoom(companyid: string): Promise<void> {
        return this.remove(this.zoomKey(companyid))
    }

    // ── Google Meet (one per company) ──────────────────────────────────────────

    static googleMeetKey(companyid: string) { return `meeting.googlemeet.${companyid}` }

    static async getGoogleMeet(companyid: string): Promise<Integration<GoogleMeetConfig> | null> {
        return this.get<GoogleMeetConfig>(this.googleMeetKey(companyid))
    }

    static async setGoogleMeet(companyid: string, config: GoogleMeetConfig, enabled = true): Promise<void> {
        await DBConfigService.setIntegration(this.googleMeetKey(companyid), 'meeting', companyid, config, enabled)
    }

    static async removeGoogleMeet(companyid: string): Promise<void> {
        return this.remove(this.googleMeetKey(companyid))
    }

    // ── All configured meeting providers (for dropdowns) ──────────────────────

    static async allMeetings(companyid: string): Promise<{ id: string; label: string; provider: 'zoom' | 'googlemeet' }[]> {
        const result: { id: string; label: string; provider: 'zoom' | 'googlemeet' }[] = []
        const zoom = await this.getZoom(companyid)
        if (zoom && zoom.enabled) {
            result.push({ id: 'zoom', label: 'Zoom', provider: 'zoom' })
        }
        const meet = await this.getGoogleMeet(companyid)
        if (meet && meet.enabled) {
            result.push({ id: 'googlemeet', label: 'Google Meet', provider: 'googlemeet' })
        }
        return result
    }
}

export default IntegrationsService
